import type { ChipProps } from '@mui/material';
import { Checkbox, TableCell, TableRow } from '@mui/material';
import type { FC } from 'react';
import { OrdersRowKeysSchema } from '~/types/OrdersTableData';
import type { OrdersTableRowProps } from './OrdersTable.types';
import { OrderTableCell } from './OrdersTableCell';

export const OrdersTableRow: FC<OrdersTableRowProps> = ({ row, index, isItemSelected, handleClick, type }) => {
  const isDisabled = row.status !== 'Awaiting Approval';
  const labelId = `shopify-orders-table-checkbox-${index}`;

  let chipColor: ChipProps['color'];
  let statusColor: string;
  switch (row.status) {
    case 'Approved':
      chipColor = 'success';
      statusColor = 'success.main';
      break;
    case 'Rejected':
      chipColor = 'error';
      statusColor = 'error.main';
      break;
    default:
      chipColor = 'warning';
      statusColor = 'warning.main';
      break;
  }

  return (
    <TableRow
      hover
      onClick={!isDisabled ? (event) => handleClick(event, row.id) : undefined}
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.id}
      selected={isItemSelected}
      sx={{
        height: 100,
        backgroundColor: 'transparent',
        cursor: !isDisabled ? 'pointer' : 'default',
        '&:nth-of-type(odd) td': { backgroundColor: 'grey.50' },
        '&:nth-of-type(even) td': { backgroundColor: 'background.paper' },
        ...(isItemSelected && {
          '& td:nth-of-type(odd), & td:nth-of-type(even)': {
            backgroundColor: 'action.selected' // TODO: needs to change, new updated DS colour has transparency which causes issues when sticky on mobile
          }
        }),
        '&:hover td:nth-of-type(odd), &:hover td:nth-of-type(even)': {
          backgroundColor: 'action.hover'
        }
      }}
      data-testid="orders-table-row"
    >
      <TableCell
        sx={{
          border: 'none',
          borderLeft: '3px solid',
          borderColor: statusColor
        }}
      >
        <Checkbox
          checked={isItemSelected}
          inputProps={{ 'aria-labelledby': labelId }}
          sx={{
            cursor: isDisabled ? 'default' : 'pointer',
            pointerEvents: isDisabled ? 'none' : 'auto',
            opacity: isDisabled ? 0.5 : 1
          }}
        />
      </TableCell>

      {Object.entries(row).map(([key]) => {
        if (key === 'id') {
          return null;
        }

        const parsedKey = OrdersRowKeysSchema.safeParse(key);
        if (parsedKey.success === false) {
          return null;
        }

        return (
          <OrderTableCell
            key={key}
            cellId={parsedKey.data}
            row={row}
            chipColor={chipColor}
            type={type}
            sx={({ palette }) => ({
              px: { xs: 1, lg: 2 },
              py: 2,
              verticalAlign: 'top',
              width: { lg: 200 },

              ...(key === 'status' && {
                position: { xs: 'sticky', lg: 'unset' },
                right: { xs: 0, lg: 'unset' },
                zIndex: { xs: 1, lg: 'unset' },
                '&:before': {
                  xs: {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '1px',
                    backgroundColor: palette.divider
                  },
                  lg: {
                    content: 'none'
                  }
                }
              })
            })}
          />
        );
      })}
    </TableRow>
  );
};
